/**
 * Helpers for project-configured crew roles on schedule-block assignments.
 *
 * Data model:
 *   - project.crew_roles  → ["lead", "crew", …] (lowercase, ordered)
 *   - assignment.is_lead = (role === "lead")
 *   - assignment.role_on_block = role name (null when role === "lead", so the
 *     LEAD label is sourced from the boolean — older data with "Lead" strings
 *     keeps working since the boolean is the source of truth).
 *
 * Display contract:
 *   - is_lead === true  → render "LEAD" text
 *   - everything else   → render a solid dot ●
 *   The role name itself is intentionally hidden from dense grid cells; it's
 *   retained on the assignment for filters, audit log, and reports.
 */

const DEFAULT_ROLES = ["lead", "crew"];

/** Returns the role list to render in the picker. Always starts with `lead`
 *  so the lead chip is the obvious first choice, even if a custom config
 *  has reordered the list. Deduplicates case-insensitively. */
export function roleOptionsFor(crewRoles) {
  const raw = Array.isArray(crewRoles) && crewRoles.length ? crewRoles : DEFAULT_ROLES;
  const seen = new Set();
  const out = [];
  // Ensure "lead" is always present and first.
  out.push("lead");
  seen.add("lead");
  for (const r of raw) {
    const key = String(r || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/** Builds the assignment payload backend expects for a given role name. */
export function payloadForRole(roleKey) {
  const key = String(roleKey || "").trim().toLowerCase();
  if (key === "lead") return { is_lead: true, role_on_block: null };
  return { is_lead: false, role_on_block: key };
}

/** Validates a candidate new role name. Returns the normalized key or null
 *  if invalid (empty, too long, or already present). */
export function normalizeNewRole(input, existing) {
  const key = String(input || "").trim().toLowerCase();
  if (!key) return null;
  if (key.length > 24) return null;
  if (!/^[a-z0-9][a-z0-9 _-]*$/.test(key)) return null;
  const set = new Set((existing || []).map((r) => String(r).trim().toLowerCase()));
  if (set.has(key)) return null;
  return key;
}
